import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { companiesAPI, contactsAPI, voipAPI, segmentsAPI, productLinesAPI, aiWorkforceAPI, tasksAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from '../components/ui/collapsible';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { PlaybookNotesSection } from '../components/PlaybookNotesSection';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft, Building2, Globe, MapPin, Phone, Mail, Users, Edit, Trash2,
  Plus, MoreVertical, ChevronDown, ChevronRight, Star, Target, DollarSign,
  Briefcase, Calendar, ExternalLink, Linkedin, Twitter, Settings, Building,
  User, TicketIcon, ShoppingCart, FileText, Clock, TrendingUp, Hash,
  Package, AlertTriangle, CheckCircle, XCircle, Shield, Bot, Sparkles,
  Loader2, Download, Lightbulb, BarChart3, Zap, Brain, MessageSquare, Link2,
  BookOpen
} from 'lucide-react';
import { AIProcessingIndicator } from '../components/AIProcessingIndicator';

const statusColors = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-slate-100 text-slate-600',
  prospect: 'bg-blue-100 text-blue-700',
  lead: 'bg-purple-100 text-purple-700',
  customer: 'bg-emerald-100 text-emerald-700',
  partner: 'bg-amber-100 text-amber-700',
  vendor: 'bg-cyan-100 text-cyan-700'
};

const productStatusColors = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-slate-100 text-slate-600',
  warranty: 'bg-blue-100 text-blue-700',
  expired: 'bg-red-100 text-red-700',
  pending_activation: 'bg-amber-100 text-amber-700'
};

const tierColors = {
  enterprise: 'bg-purple-100 text-purple-700',
  mid_market: 'bg-blue-100 text-blue-700',
  smb: 'bg-emerald-100 text-emerald-700',
  startup: 'bg-amber-100 text-amber-700'
};

const unitTypeLabels = {
  branch: 'Branch',
  division: 'Division',
  department: 'Department',
  location: 'Location',
  subsidiary: 'Subsidiary'
};

export const CompanyProfilePage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [editCompanyDialog, setEditCompanyDialog] = useState(false);
  const [unitDialog, setUnitDialog] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  
  // Products (Asset Tracking)
  const [products, setProducts] = useState([]);
  const [productStats, setProductStats] = useState(null);
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    product_name: '',
    serial_number: '',
    product_type: 'hardware',
    model_number: '',
    purchase_date: '',
    warranty_expiry: '',
    support_expiry: '',
    support_level: 'standard',
    status: 'active',
    location: '',
    license_key: '',
    quantity: 1,
    notes: ''
  });
  
  // Sections open state
  const [aboutOpen, setAboutOpen] = useState(true);
  const [unitsOpen, setUnitsOpen] = useState(true);
  const [contactsOpen, setContactsOpen] = useState(true);
  const [productsOpen, setProductsOpen] = useState(true);
  
  // Form states
  const [segments, setSegments] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  
  const [companyForm, setCompanyForm] = useState({});
  const [unitForm, setUnitForm] = useState({
    name: '',
    unit_type: 'branch',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    phone: '',
    email: '',
    manager_contact_id: '',
    employee_count: '',
    is_headquarters: false,
    notes: ''
  });
  
  const [saving, setSaving] = useState(false);
  
  // Link Contact Dialog state
  const [linkContactDialog, setLinkContactDialog] = useState(false);
  const [unlinkedContacts, setUnlinkedContacts] = useState([]);
  const [selectedContactToLink, setSelectedContactToLink] = useState('');
  const [linkingContact, setLinkingContact] = useState(false);
  
  // AI Sales Tools state
  const [aiAgents, setAiAgents] = useState([]);
  const [companyTasks, setCompanyTasks] = useState([]);
  const [showAITaskDialog, setShowAITaskDialog] = useState(false);
  const [creatingAITask, setCreatingAITask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskAIHistory, setTaskAIHistory] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [aiTaskForm, setAiTaskForm] = useState({
    title: '',
    description: '',
    assigned_agent_id: '',
    deliverable_type: 'document',
    priority: 'medium'
  });
  
  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [createdTaskIds, setCreatedTaskIds] = useState(new Set()); // Track which suggestions became tasks

  // Tickets & Orders state
  const [companyTickets, setCompanyTickets] = useState([]);
  const [companyOrders, setCompanyOrders] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    fetchCompanyProfile();
    fetchSegments();
    fetchProductLines();
    fetchAllContacts();
    fetchProducts();
    fetchAIAgents();
    fetchCompanyTasks();
    fetchCompanyTickets();
    fetchCompanyOrders();
  }, [companyId]);

  const fetchCompanyProfile = async () => {
    setLoading(true);
    try {
      const res = await companiesAPI.getProfile(companyId);
      setCompany(res.data);
    } catch (error) {
      toast.error('Failed to load company profile');
      navigate('/companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchSegments = async () => {
    try {
      const res = await segmentsAPI.getAll();
      setSegments(res.data?.segments || []);
    } catch (error) {
      console.error('Failed to fetch segments');
    }
  };

  const fetchProductLines = async () => {
    try {
      const res = await productLinesAPI.getAll();
      setProductLines(res.data || []);
    } catch (error) {
      console.error('Failed to fetch product lines');
    }
  };

  const fetchAllContacts = async () => {
    try {
      const res = await contactsAPI.getAll();
      setAllContacts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch contacts');
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await companiesAPI.getProducts(companyId);
      setProducts(res.data.products || []);
      setProductStats(res.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch products');
    }
  };

  const fetchAIAgents = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-workforce/agents?status=active`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAiAgents(data);
      }
    } catch (error) {
      console.error('Failed to fetch AI agents');
    }
  };

  const fetchCompanyTasks = async () => {
    try {
      const res = await tasksAPI.getAll();
      // Filter tasks for this company
      const allTasks = res.data || [];
      const filtered = allTasks.filter(t => t.company_id === companyId);
      setCompanyTasks(filtered);
    } catch (error) {
      console.error('Failed to fetch company tasks');
    }
  };

  const fetchCompanyTickets = async () => {
    setLoadingTickets(true);
    try {
      const res = await companiesAPI.getTickets(companyId, { limit: 50 });
      setCompanyTickets(res.data || []);
    } catch (error) {
      console.error('Failed to fetch company tickets');
    } finally {
      setLoadingTickets(false);
    }
  };

  const fetchCompanyOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await companiesAPI.getOrders(companyId, { limit: 50 });
      setCompanyOrders(res.data || []);
    } catch (error) {
      console.error('Failed to fetch company orders');
    } finally {
      setLoadingOrders(false);
    }
  };

  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        product_name: product.product_name || '',
        serial_number: product.serial_number || '',
        product_type: product.product_type || 'hardware',
        model_number: product.model_number || '',
        purchase_date: product.purchase_date?.split('T')[0] || '',
        warranty_expiry: product.warranty_expiry?.split('T')[0] || '',
        support_expiry: product.support_expiry?.split('T')[0] || '',
        support_level: product.support_level || 'standard',
        status: product.status || 'active',
        location: product.location || '',
        license_key: product.license_key || '',
        quantity: product.quantity || 1,
        notes: product.notes || ''
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        product_name: '',
        serial_number: '',
        product_type: 'hardware',
        model_number: '',
        purchase_date: '',
        warranty_expiry: '',
        support_expiry: '',
        support_level: 'standard',
        status: 'active',
        location: '',
        license_key: '',
        quantity: 1,
        notes: ''
      });
    }
    setProductDialog(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.product_name || !productForm.serial_number) {
      toast.error('Product name and serial number are required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingProduct) {
        await companiesAPI.updateProduct(companyId, editingProduct.id, productForm);
        toast.success('Product updated');
      } else {
        await companiesAPI.createProduct(companyId, { ...productForm, company_id: companyId });
        toast.success('Product added');
      }
      setProductDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await companiesAPI.deleteProduct(companyId, productId);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Fetch contacts not linked to any company (for Link Contact feature)
  const fetchUnlinkedContacts = async () => {
    try {
      const response = await contactsAPI.getAll();
      const allContacts = response.data || [];
      // Filter to only show contacts not already linked to this company
      const unlinked = allContacts.filter(c => !c.company_id || c.company_id === '');
      setUnlinkedContacts(unlinked);
    } catch (error) {
      console.error('Failed to fetch unlinked contacts:', error);
      toast.error('Failed to load contacts');
    }
  };

  // Link existing contact to this company
  const handleLinkContact = async () => {
    if (!selectedContactToLink) {
      toast.error('Please select a contact');
      return;
    }

    setLinkingContact(true);
    try {
      await contactsAPI.update(selectedContactToLink, { company_id: companyId });
      toast.success('Contact linked to company');
      setLinkContactDialog(false);
      setSelectedContactToLink('');
      fetchCompanyProfile(); // Refresh to get updated contacts list
    } catch (error) {
      toast.error('Failed to link contact');
    } finally {
      setLinkingContact(false);
    }
  };

  const openEditCompanyDialog = () => {
    if (company) {
      setCompanyForm({
        name: company.name || '',
        legal_name: company.legal_name || '',
        industry: company.industry || '',
        website: company.website || '',
        phone: company.phone || '',
        email: company.email || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        country: company.country || '',
        postal_code: company.postal_code || '',
        employee_count: company.employee_count?.toString() || '',
        annual_revenue: company.annual_revenue?.toString() || '',
        status: company.status || 'active',
        account_type: company.account_type || 'prospect',
        tier: company.tier || '',
        priority: company.priority || '',
        key_contact_id: company.key_contact_id || '',
        payment_terms: company.payment_terms || '',
        awareness_level: company.awareness_level || '',
        description: company.description || '',
        notes: company.notes || '',
        linkedin_url: company.linkedin_url || '',
        twitter_handle: company.twitter_handle || ''
      });
      setEditCompanyDialog(true);
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updateData = {
        ...companyForm,
        employee_count: companyForm.employee_count ? parseInt(companyForm.employee_count) : null,
        annual_revenue: companyForm.annual_revenue ? parseFloat(companyForm.annual_revenue) : null,
        key_contact_id: companyForm.key_contact_id || null,
        tier: companyForm.tier || null,
        priority: companyForm.priority || null,
        awareness_level: companyForm.awareness_level || null
      };
      await companiesAPI.update(companyId, updateData);
      toast.success('Company updated');
      setEditCompanyDialog(false);
      fetchCompanyProfile();
    } catch (error) {
      toast.error('Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const openUnitDialog = (unit = null) => {
    if (unit) {
      setEditingUnit(unit);
      setUnitForm({
        name: unit.name || '',
        unit_type: unit.unit_type || 'branch',
        address: unit.address || '',
        city: unit.city || '',
        state: unit.state || '',
        country: unit.country || '',
        postal_code: unit.postal_code || '',
        phone: unit.phone || '',
        email: unit.email || '',
        manager_contact_id: unit.manager_contact_id || '',
        employee_count: unit.employee_count?.toString() || '',
        is_headquarters: unit.is_headquarters || false,
        notes: unit.notes || ''
      });
    } else {
      setEditingUnit(null);
      setUnitForm({
        name: '',
        unit_type: 'branch',
        address: '',
        city: '',
        state: '',
        country: '',
        postal_code: '',
        phone: '',
        email: '',
        manager_contact_id: '',
        employee_count: '',
        is_headquarters: false,
        notes: ''
      });
    }
    setUnitDialog(true);
  };

  const handleSaveUnit = async (e) => {
    e.preventDefault();
    if (!unitForm.name) {
      toast.error('Unit name is required');
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...unitForm,
        company_id: companyId,
        employee_count: unitForm.employee_count ? parseInt(unitForm.employee_count) : null,
        manager_contact_id: unitForm.manager_contact_id || null
      };
      
      if (editingUnit) {
        await companiesAPI.updateUnit(editingUnit.id, data);
        toast.success('Unit updated');
      } else {
        await companiesAPI.createUnit(companyId, data);
        toast.success('Unit created');
      }
      setUnitDialog(false);
      fetchCompanyProfile();
    } catch (error) {
      toast.error('Failed to save unit');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;
    try {
      await companiesAPI.deleteUnit(unitId);
      toast.success('Unit deleted');
      fetchCompanyProfile();
    } catch (error) {
      toast.error('Failed to delete unit');
    }
  };

  const handleClickToCall = async (phoneNumber, name) => {
    if (!phoneNumber) {
      toast.error('No phone number available');
      return;
    }
    try {
      await voipAPI.initiateCall({ phone_number: phoneNumber, contact_name: name });
      toast.success(`Initiating call to ${name}`);
    } catch (error) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  // AI Task Handlers
  const handleCreateAITask = async (e) => {
    e.preventDefault();
    if (!aiTaskForm.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    
    setCreatingAITask(true);
    try {
      // Determine assignment info
      let assignedToType = 'user';
      let assignedToName = '';
      let assignedTo = null;
      
      if (aiTaskForm.assigned_agent_id) {
        assignedToType = 'ai_agent';
        assignedTo = aiTaskForm.assigned_agent_id;
        const agent = aiAgents.find(a => a.id === aiTaskForm.assigned_agent_id);
        assignedToName = agent?.name || agent?.display_name || 'AI Agent';
      }
      
      const taskData = {
        title: aiTaskForm.title,
        description: aiTaskForm.description || '',
        priority: aiTaskForm.priority,
        status: 'pending',
        company_id: companyId,
        assigned_to: assignedTo,
        assigned_to_type: assignedToType,
        assigned_to_name: assignedToName,
        deliverable_type: assignedToType === 'ai_agent' ? aiTaskForm.deliverable_type : null
      };
      
      await tasksAPI.create(taskData);
      toast.success('AI Task created and queued for processing');
      
      // Reset form and close dialog
      setAiTaskForm({
        title: '',
        description: '',
        assigned_agent_id: '',
        deliverable_type: 'document',
        priority: 'medium'
      });
      setShowAITaskDialog(false);
      fetchCompanyTasks();
    } catch (error) {
      console.error('Failed to create AI task:', error);
      toast.error(error.response?.data?.detail || 'Failed to create AI task');
    } finally {
      setCreatingAITask(false);
    }
  };

  const handleViewTaskDetail = async (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
    
    // Fetch AI history if it's an AI task
    if (task.assigned_to_type === 'ai_agent') {
      try {
        const res = await tasksAPI.getAIHistory(task.id);
        setTaskAIHistory(res.data);
      } catch (error) {
        console.error('Failed to fetch AI history:', error);
        setTaskAIHistory(null);
      }
    } else {
      setTaskAIHistory(null);
    }
  };

  const handleDownloadDeliverable = async (deliverable) => {
    try {
      const downloadUrl = deliverable.url || deliverable.file_url;
      if (!downloadUrl) {
        toast.error('Download URL not available');
        return;
      }
      
      const fullUrl = downloadUrl.startsWith('http') ? downloadUrl : `${process.env.REACT_APP_BACKEND_URL}${downloadUrl}`;
      const token = localStorage.getItem('token');
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const contentType = response.headers.get('content-type') || 'application/octet-stream';
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      // Ensure proper file extension (Bug #24 fix)
      let filename = deliverable.filename || deliverable.title || 'document';
      if (!filename.includes('.')) {
        if (contentType.includes('pdf')) filename += '.pdf';
        else if (contentType.includes('word') || contentType.includes('docx')) filename += '.docx';
        else if (contentType.includes('text')) filename += '.txt';
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
      toast.success('Download complete!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  // AI Analysis Handler
  const generateAIAnalysis = async () => {
    setLoadingAnalysis(true);
    setAnalysisError(null);
    setCreatedTaskIds(new Set()); // Reset created tasks on new analysis
    
    try {
      const res = await aiWorkforceAPI.getCompanyAnalysis?.(companyId) || 
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-workforce/company/${companyId}/analysis`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ company_id: companyId })
        }).then(r => r.ok ? r.json() : Promise.reject(r));
      
      setAiAnalysis(res.data || res);
      toast.success('AI Analysis generated');
    } catch (error) {
      console.error('Failed to generate AI analysis:', error);
      setAnalysisError('Failed to generate AI analysis. Please try again.');
      toast.error('Failed to generate AI analysis');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  // Create a task from AI suggestion
  const createTaskFromSuggestion = async (suggestion, type) => {
    const suggestionId = `${type}-${suggestion.title || suggestion}`;
    
    if (createdTaskIds.has(suggestionId)) {
      toast.info('Task already created');
      return;
    }
    
    try {
      const taskData = {
        title: suggestion.title || suggestion,
        description: suggestion.description || suggestion.expected_impact || '',
        priority: suggestion.priority === 'high' ? 'high' : suggestion.confidence === 'high' ? 'high' : 'medium',
        status: 'pending',
        company_id: companyId,
        tags: [`ai_suggested`, `ai_${type}`],
        source: 'ai_analysis'
      };
      
      await tasksAPI.create(taskData);
      
      setCreatedTaskIds(prev => new Set([...prev, suggestionId]));
      toast.success('Task created from AI suggestion');
      fetchCompanyTasks(); // Refresh task list
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    }
  };

  // Create all suggested tasks at once
  const createAllSuggestedTasks = async () => {
    if (!aiAnalysis) return;
    
    const tasks = [];
    
    // Add sales opportunities as tasks
    if (aiAnalysis.sales_prospects?.opportunities) {
      aiAnalysis.sales_prospects.opportunities.forEach(opp => {
        tasks.push({ suggestion: opp, type: 'sales_opportunity' });
      });
    }
    
    // Add support strategies as tasks
    if (aiAnalysis.support_strategies?.recommendations) {
      aiAnalysis.support_strategies.recommendations.forEach(rec => {
        tasks.push({ suggestion: rec, type: 'support_strategy' });
      });
    }
    
    // Add suggested_tasks if present
    if (aiAnalysis.suggested_tasks) {
      aiAnalysis.suggested_tasks.forEach(task => {
        tasks.push({ suggestion: task, type: 'suggested_task' });
      });
    }
    
    if (tasks.length === 0) {
      toast.info('No actionable suggestions to create tasks from');
      return;
    }
    
    let created = 0;
    for (const { suggestion, type } of tasks) {
      const suggestionId = `${type}-${suggestion.title || suggestion}`;
      if (!createdTaskIds.has(suggestionId)) {
        try {
          await createTaskFromSuggestion(suggestion, type);
          created++;
        } catch (e) {
          // Continue with other tasks
        }
      }
    }
    
    if (created > 0) {
      toast.success(`Created ${created} tasks from AI suggestions`);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!company) return null;

  const companyContacts = company.contacts || [];
  const linkedContacts = allContacts.filter(c => c.company_id === companyId);
  
  // Calculate product type summary for segmentation
  const productTypeSummary = products.reduce((acc, p) => {
    const type = p.product_type || 'other';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Determine customer awareness level based on activity and engagement
  const getAwarenessLevel = () => {
    const hasDeals = (company.deal_count || 0) > 0;
    const hasOrders = (company.order_count || 0) > 0;
    const hasProducts = products.length > 0;
    const recentActivity = company.recent_activities?.length > 0;
    
    if (hasOrders && hasProducts && recentActivity) return 'engaged';
    if (hasDeals || hasProducts) return 'active';
    if (recentActivity) return 'aware';
    return 'new';
  };
  
  const awarenessLevel = company.awareness_level || getAwarenessLevel();
  const awarenessConfig = {
    engaged: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Engaged', icon: '🔥' },
    active: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Active', icon: '✓' },
    aware: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Aware', icon: '👁' },
    new: { color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'New', icon: '🆕' },
    at_risk: { color: 'bg-red-100 text-red-700 border-red-200', label: 'At Risk', icon: '⚠' }
  };

  return (
    <div className="space-y-6" data-testid="company-profile-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/companies')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="h-16 w-16 rounded-lg bg-slate-100 flex items-center justify-center">
            <Building2 className="h-8 w-8 text-slate-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{company.name}</h1>
              {company.tier && (
                <Badge className={tierColors[company.tier]}>
                  {company.tier.replace('_', ' ')}
                </Badge>
              )}
              {/* Customer Awareness Badge */}
              <Badge className={`${awarenessConfig[awarenessLevel]?.color} border`} data-testid="awareness-badge">
                <span className="mr-1">{awarenessConfig[awarenessLevel]?.icon}</span>
                {awarenessConfig[awarenessLevel]?.label}
              </Badge>
              {/* Payment Terms Badge */}
              {company.payment_terms && (
                <Badge variant="outline" className="border-slate-300">
                  <DollarSign className="h-3 w-3 mr-1" />
                  {company.payment_terms}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-muted-foreground">
              {company.industry && <span>{company.industry}</span>}
              {company.website && (
                <a 
                  href={company.website.startsWith('http') ? company.website : `https://${company.website}`} 
                  target="_blank" 
                  rel="noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:underline"
                >
                  <Globe className="h-4 w-4" />
                  Website
                </a>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Actions <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={openEditCompanyDialog}>
                <Edit className="h-4 w-4 mr-2" /> Edit Company
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openUnitDialog()}>
                <Plus className="h-4 w-4 mr-2" /> Add Unit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/contacts?company_id=${companyId}`)}>
                <Users className="h-4 w-4 mr-2" /> View Contacts
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{company.contact_count || 0}</p>
                <p className="text-xs text-muted-foreground">Contacts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Target className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{company.deal_count || 0}</p>
                <p className="text-xs text-muted-foreground">Deals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xl font-bold">{formatCurrency(company.total_deal_value)}</p>
                <p className="text-xs text-muted-foreground">Deal Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{company.order_count || 0}</p>
                <p className="text-xs text-muted-foreground">Orders</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <TicketIcon className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{company.open_tickets || 0}</p>
                <p className="text-xs text-muted-foreground">Open Tickets</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Product Segmentation Bar */}
      {products.length > 0 && (
        <Card className="overflow-hidden">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-600">Product Segments</span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {Object.entries(productTypeSummary).map(([type, count]) => (
                  <div key={type} className="flex items-center gap-1.5">
                    <Badge 
                      variant="outline" 
                      className={
                        type === 'hardware' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                        type === 'software' ? 'bg-purple-50 border-purple-200 text-purple-700' :
                        type === 'service' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                        type === 'subscription' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                        'bg-slate-50 border-slate-200 text-slate-600'
                      }
                    >
                      <span className="capitalize">{type}</span>
                      <span className="ml-1 px-1.5 py-0.5 bg-white/50 rounded text-xs font-bold">{count}</span>
                    </Badge>
                  </div>
                ))}
                <span className="text-xs text-muted-foreground">
                  {products.length} total product{products.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Company Details */}
        <div className="lg:col-span-1 space-y-4">
          {/* About */}
          <Collapsible open={aboutOpen} onOpenChange={setAboutOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    {aboutOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    About this company
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); openEditCompanyDialog(); }}
                  >
                    <Settings className="h-3 w-3" />
                  </Button>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-4">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge className={statusColors[company.status]}>{company.status}</Badge>
                  </div>
                  
                  {/* Account Type */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Account Type</span>
                    <span className="text-sm font-medium capitalize">{company.account_type}</span>
                  </div>
                  
                  {/* Key Contact */}
                  {company.key_contact && (
                    <div>
                      <span className="text-sm text-muted-foreground">Key Contact</span>
                      <div className="flex items-center gap-2 mt-1">
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                        <Link to={`/contacts/${company.key_contact_id}`} className="text-sm font-medium hover:underline">
                          {company.key_contact.first_name} {company.key_contact.last_name}
                        </Link>
                      </div>
                      {company.key_contact.job_title && (
                        <p className="text-xs text-muted-foreground ml-6">{company.key_contact.job_title}</p>
                      )}
                    </div>
                  )}

                  <Separator />

                  {/* Contact Info */}
                  {company.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <button 
                        onClick={() => handleClickToCall(company.phone, company.name)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {company.phone}
                      </button>
                    </div>
                  )}
                  
                  {company.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <a href={`mailto:${company.email}`} className="text-sm text-blue-600 hover:underline">
                        {company.email}
                      </a>
                    </div>
                  )}
                  
                  {(company.address || company.city || company.country) && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div className="text-sm">
                        {company.address && <p>{company.address}</p>}
                        <p>{[company.city, company.state, company.postal_code].filter(Boolean).join(', ')}</p>
                        {company.country && <p>{company.country}</p>}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Business Info */}
                  {company.employee_count && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Employees</span>
                      <span className="text-sm font-medium">{company.employee_count.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {company.annual_revenue && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Annual Revenue</span>
                      <span className="text-sm font-medium">{formatCurrency(company.annual_revenue)}</span>
                    </div>
                  )}
                  
                  {company.founded_year && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Founded</span>
                      <span className="text-sm font-medium">{company.founded_year}</span>
                    </div>
                  )}

                  {/* Social Links */}
                  {(company.linkedin_url || company.twitter_handle) && (
                    <>
                      <Separator />
                      <div className="flex items-center gap-3">
                        {company.linkedin_url && (
                          <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">
                            <Linkedin className="h-5 w-5" />
                          </a>
                        )}
                        {company.twitter_handle && (
                          <a href={`https://twitter.com/${company.twitter_handle}`} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-600">
                            <Twitter className="h-5 w-5" />
                          </a>
                        )}
                      </div>
                    </>
                  )}

                  {/* Description */}
                  {company.description && (
                    <>
                      <Separator />
                      <div>
                        <span className="text-sm text-muted-foreground">Description</span>
                        <p className="text-sm mt-1">{company.description}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Units */}
          <Collapsible open={unitsOpen} onOpenChange={setUnitsOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    {unitsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    Units & Locations ({company.units?.length || 0})
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); openUnitDialog(); }}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-3">
                  {company.units?.length > 0 ? (
                    company.units.map(unit => (
                      <div 
                        key={unit.id} 
                        className="p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{unit.name}</span>
                              {unit.is_headquarters && (
                                <Badge variant="outline" className="text-xs">HQ</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground ml-6 mt-1">
                              {unitTypeLabels[unit.unit_type]} • {unit.contact_count || 0} contacts
                            </p>
                            {unit.city && (
                              <p className="text-xs text-muted-foreground ml-6">
                                {[unit.city, unit.state, unit.country].filter(Boolean).join(', ')}
                              </p>
                            )}
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openUnitDialog(unit)}>
                                <Edit className="h-4 w-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteUnit(unit.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No units added yet
                    </p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Products / Assets Section */}
          <Collapsible open={productsOpen} onOpenChange={setProductsOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    {productsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <CardTitle className="flex-1 text-left ml-2 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Products & Assets
                      <Badge variant="outline" className="ml-2">{products.length}</Badge>
                    </CardTitle>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); openProductDialog(); }}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  {/* Product Stats */}
                  {productStats && (
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      <div className="p-2 bg-slate-50 rounded text-center">
                        <p className="text-lg font-bold">{productStats.total}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                      <div className="p-2 bg-emerald-50 rounded text-center">
                        <p className="text-lg font-bold text-emerald-700">{productStats.active || 0}</p>
                        <p className="text-xs text-muted-foreground">Active</p>
                      </div>
                      <div className="p-2 bg-amber-50 rounded text-center">
                        <p className="text-lg font-bold text-amber-700">{productStats.expiring_soon || 0}</p>
                        <p className="text-xs text-muted-foreground">Expiring Soon</p>
                      </div>
                      <div className="p-2 bg-red-50 rounded text-center">
                        <p className="text-lg font-bold text-red-700">{productStats.expired_support || 0}</p>
                        <p className="text-xs text-muted-foreground">Expired</p>
                      </div>
                    </div>
                  )}

                  {products.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No products tracked yet</p>
                      <Button variant="outline" size="sm" className="mt-2" onClick={() => openProductDialog()}>
                        <Plus className="h-4 w-4 mr-1" /> Add Product
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {products.map(product => (
                        <div 
                          key={product.id} 
                          className="p-3 border rounded-lg hover:bg-slate-50 group"
                          data-testid={`product-${product.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{product.product_name}</span>
                                <Badge className={productStatusColors[product.status] || 'bg-slate-100'}>
                                  {product.status}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Hash className="h-3 w-3" />
                                  {product.serial_number}
                                </span>
                                {product.product_type && (
                                  <span className="capitalize">{product.product_type}</span>
                                )}
                                {product.model_number && (
                                  <span>Model: {product.model_number}</span>
                                )}
                              </div>
                              {/* Support/Warranty Info */}
                              <div className="flex items-center gap-4 mt-2 text-xs">
                                {product.support_expiry && (
                                  <span className={`flex items-center gap-1 ${
                                    new Date(product.support_expiry) < new Date() ? 'text-red-600' :
                                    new Date(product.support_expiry) < new Date(Date.now() + 30*24*60*60*1000) ? 'text-amber-600' :
                                    'text-emerald-600'
                                  }`}>
                                    <Shield className="h-3 w-3" />
                                    Support: {format(new Date(product.support_expiry), 'MMM d, yyyy')}
                                  </span>
                                )}
                                {product.warranty_expiry && (
                                  <span className={`flex items-center gap-1 ${
                                    new Date(product.warranty_expiry) < new Date() ? 'text-red-600' : 'text-blue-600'
                                  }`}>
                                    <CheckCircle className="h-3 w-3" />
                                    Warranty: {format(new Date(product.warranty_expiry), 'MMM d, yyyy')}
                                  </span>
                                )}
                                {product.quantity > 1 && (
                                  <span>Qty: {product.quantity}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" onClick={() => openProductDialog(product)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(product.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>

        {/* Right Column - Tabs */}
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="contacts">Contacts</TabsTrigger>
              <TabsTrigger value="deals">Deals</TabsTrigger>
              <TabsTrigger value="tasks" className="flex items-center gap-1">
                Tasks {companyTasks.length > 0 && <Badge variant="secondary" className="ml-1 h-5 px-1.5">{companyTasks.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="tickets-orders" className="flex items-center gap-1">
                <TicketIcon className="h-3 w-3" /> Support
                {(companyTickets.length + companyOrders.length) > 0 && <Badge variant="secondary" className="ml-1 h-5 px-1.5">{companyTickets.length + companyOrders.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="ai-tools" className="flex items-center gap-1">
                <Bot className="h-3 w-3" /> AI
              </TabsTrigger>
              <TabsTrigger value="ai-analysis" className="flex items-center gap-1">
                <Brain className="h-3 w-3" /> Insights
              </TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="playbooks" className="flex items-center gap-1">
                <BookOpen className="h-3 w-3" /> Playbooks
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              {/* Financial Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Financial Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Revenue</p>
                      <p className="text-lg font-bold">{formatCurrency(company.total_order_value)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Pipeline Value</p>
                      <p className="text-lg font-bold">{formatCurrency(company.total_deal_value)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Payment Terms</p>
                      <p className="text-lg font-medium">{company.payment_terms || 'Not set'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Parent Company / Subsidiaries */}
              {(company.parent_company || company.subsidiaries?.length > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Company Structure</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {company.parent_company && (
                      <div>
                        <p className="text-sm text-muted-foreground">Parent Company</p>
                        <Link 
                          to={`/companies/${company.parent_company.id}`}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {company.parent_company.name}
                        </Link>
                      </div>
                    )}
                    {company.subsidiaries?.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Subsidiaries</p>
                        <div className="space-y-1">
                          {company.subsidiaries.map(sub => (
                            <Link 
                              key={sub.id}
                              to={`/companies/${sub.id}`}
                              className="block text-blue-600 hover:underline"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              {company.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap">{company.notes}</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="contacts" className="mt-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Contacts ({companyContacts.length})</CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => {
                        fetchUnlinkedContacts();
                        setLinkContactDialog(true);
                      }}
                      data-testid="link-contact-btn"
                    >
                      <Link2 className="h-4 w-4 mr-1" /> Link Existing
                    </Button>
                    <Button size="sm" onClick={() => navigate(`/contacts?company_id=${companyId}`)}>
                      <Plus className="h-4 w-4 mr-1" /> Add New
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {companyContacts.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {companyContacts.map(contact => (
                          <TableRow key={contact.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {contact.id === company.key_contact_id && (
                                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                                )}
                                <Link 
                                  to={`/contacts/${contact.id}`}
                                  className="font-medium hover:underline"
                                >
                                  {contact.first_name} {contact.last_name}
                                </Link>
                              </div>
                            </TableCell>
                            <TableCell>{contact.job_title || '-'}</TableCell>
                            <TableCell>
                              <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                                {contact.email}
                              </a>
                            </TableCell>
                            <TableCell>
                              {contact.phone ? (
                                <button 
                                  onClick={() => handleClickToCall(contact.phone, `${contact.first_name} ${contact.last_name}`)}
                                  className="text-blue-600 hover:underline"
                                >
                                  {contact.phone}
                                </button>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No contacts linked to this company</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="deals" className="mt-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Deals ({company.deal_count || 0})</CardTitle>
                  <Button size="sm" onClick={() => navigate('/deals')}>
                    <Plus className="h-4 w-4 mr-1" /> New Deal
                  </Button>
                </CardHeader>
                <CardContent>
                  {company.deals?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Deal Name</TableHead>
                          <TableHead>Stage</TableHead>
                          <TableHead>Value</TableHead>
                          <TableHead>Close Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {company.deals.map(deal => (
                          <TableRow key={deal.id}>
                            <TableCell>
                              <Link to={`/deals/${deal.id}`} className="font-medium hover:underline">
                                {deal.name}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{deal.stage}</Badge>
                            </TableCell>
                            <TableCell>{formatCurrency(deal.value)}</TableCell>
                            <TableCell>
                              {deal.expected_close_date 
                                ? format(new Date(deal.expected_close_date), 'MMM d, yyyy')
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No deals for this company</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tasks Tab */}
            <TabsContent value="tasks" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Company Tasks</CardTitle>
                    <CardDescription>
                      All tasks associated with {company?.name}
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowAITaskDialog(true)} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Task
                  </Button>
                </CardHeader>
                <CardContent>
                  {companyTasks.length > 0 ? (
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-3">
                        {companyTasks.map(task => (
                          <div 
                            key={task.id}
                            className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                            onClick={() => handleViewTaskDetail(task)}
                            data-testid={`task-row-${task.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                  task.assigned_to_type === 'ai_agent' 
                                    ? 'bg-purple-100' 
                                    : 'bg-blue-100'
                                }`}>
                                  {task.assigned_to_type === 'ai_agent' ? (
                                    <Bot className="h-5 w-5 text-purple-600" />
                                  ) : (
                                    <User className="h-5 w-5 text-blue-600" />
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{task.title}</p>
                                    {task.source === 'ai_analysis' && (
                                      <Badge variant="outline" className="text-xs bg-purple-50 border-purple-200 text-purple-700">
                                        <Sparkles className="h-3 w-3 mr-1" />
                                        AI Suggested
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {task.assigned_to_name || (task.assigned_to_type === 'ai_agent' ? 'AI Agent' : 'Unassigned')}
                                  </p>
                                  {task.description && (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                      {task.description}
                                    </p>
                                  )}
                                  {task.tags?.length > 0 && (
                                    <div className="flex gap-1 mt-2 flex-wrap">
                                      {task.tags.slice(0, 3).map(tag => (
                                        <Badge key={tag} variant="secondary" className="text-xs">
                                          {tag.replace('ai_', '').replace('_', ' ')}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <Badge className={
                                  task.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                  task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  task.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                  'bg-slate-100 text-slate-700'
                                }>
                                  {task.status?.replace('_', ' ')}
                                </Badge>
                                {task.assigned_to_type === 'ai_agent' && (
                                  <AIProcessingIndicator taskId={task.id} compact />
                                )}
                                <Badge variant="outline" className={
                                  task.priority === 'high' ? 'border-red-300 text-red-700' :
                                  task.priority === 'medium' ? 'border-amber-300 text-amber-700' :
                                  'border-slate-300 text-slate-600'
                                }>
                                  {task.priority}
                                </Badge>
                              </div>
                            </div>
                            {task.created_at && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Created: {format(new Date(task.created_at), 'MMM d, yyyy h:mm a')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="h-16 w-16 text-slate-200 mx-auto mb-4" />
                      <h3 className="font-medium text-lg mb-2">No Tasks Yet</h3>
                      <p className="text-muted-foreground text-sm mb-4">
                        Create tasks or generate AI analysis to get suggested action items
                      </p>
                      <div className="flex justify-center gap-2">
                        <Button onClick={() => setShowAITaskDialog(true)} variant="outline">
                          <Plus className="h-4 w-4 mr-2" />
                          Create Task
                        </Button>
                        <Button onClick={() => setActiveTab('ai-analysis')} variant="outline">
                          <Brain className="h-4 w-4 mr-2" />
                          Generate AI Insights
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tickets & Orders Tab */}
            <TabsContent value="tickets-orders" className="mt-4 space-y-4">
              {/* Tickets Section */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TicketIcon className="h-5 w-5 text-red-600" />
                      Support Tickets
                    </CardTitle>
                    <CardDescription>
                      {companyTickets.length} ticket{companyTickets.length !== 1 ? 's' : ''} for {company?.name}
                    </CardDescription>
                  </div>
                  <Link to="/tickets">
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      New Ticket
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {loadingTickets ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : companyTickets.length > 0 ? (
                    <ScrollArea className="h-[250px]">
                      <div className="space-y-2">
                        {companyTickets.map(ticket => (
                          <Link 
                            key={ticket.id} 
                            to={`/tickets?id=${ticket.id}`}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                ticket.priority === 'urgent' ? 'bg-red-100' :
                                ticket.priority === 'high' ? 'bg-orange-100' :
                                'bg-slate-100'
                              }`}>
                                <TicketIcon className={`h-4 w-4 ${
                                  ticket.priority === 'urgent' ? 'text-red-600' :
                                  ticket.priority === 'high' ? 'text-orange-600' :
                                  'text-slate-600'
                                }`} />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{ticket.subject}</p>
                                <p className="text-xs text-muted-foreground">
                                  {ticket.category} • {format(new Date(ticket.created_at), 'MMM d, yyyy')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={
                                ticket.status === 'open' ? 'bg-red-100 text-red-700' :
                                ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                ticket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {ticket.status?.replace('_', ' ')}
                              </Badge>
                              <Badge variant="outline" className={
                                ticket.priority === 'urgent' ? 'border-red-300 text-red-700' :
                                ticket.priority === 'high' ? 'border-orange-300 text-orange-700' :
                                'border-slate-300'
                              }>
                                {ticket.priority}
                              </Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="text-center py-8">
                      <TicketIcon className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm">No tickets for this company</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Orders Section */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5 text-blue-600" />
                      Orders
                    </CardTitle>
                    <CardDescription>
                      {companyOrders.length} order{companyOrders.length !== 1 ? 's' : ''} for {company?.name}
                    </CardDescription>
                  </div>
                  <Link to="/orders">
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      New Order
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {loadingOrders ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : companyOrders.length > 0 ? (
                    <ScrollArea className="h-[250px]">
                      <div className="space-y-2">
                        {companyOrders.map(order => (
                          <Link 
                            key={order.id} 
                            to={`/orders?id=${order.id}`}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <ShoppingCart className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">Order #{order.order_number || order.id?.slice(-6)}</p>
                                <p className="text-xs text-muted-foreground">
                                  {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''} • {format(new Date(order.created_at), 'MMM d, yyyy')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={
                                order.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                order.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                                order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                                order.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                                order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {order.status}
                              </Badge>
                              <span className="font-medium text-sm">
                                {formatCurrency(order.total || 0)}
                              </span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="text-center py-8">
                      <ShoppingCart className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm">No orders for this company</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* AI Sales Tools Tab */}
            <TabsContent value="ai-tools" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Bot className="h-5 w-5 text-purple-600" />
                      AI Sales Tools
                    </CardTitle>
                    <CardDescription>
                      Assign tasks to AI agents to generate deliverables for this company
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowAITaskDialog(true)} data-testid="create-ai-task-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create AI Task
                  </Button>
                </CardHeader>
                <CardContent>
                  {/* AI Tasks List */}
                  {companyTasks.filter(t => t.assigned_to_type === 'ai_agent').length > 0 ? (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3">
                        {companyTasks.filter(t => t.assigned_to_type === 'ai_agent').map(task => (
                          <div 
                            key={task.id}
                            className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                            onClick={() => handleViewTaskDetail(task)}
                            data-testid={`ai-task-${task.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                                  <Bot className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{task.title}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {task.assigned_to_name || 'AI Agent'}
                                  </p>
                                  {task.description && (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                      {task.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  task.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                  task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  task.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                  'bg-slate-100 text-slate-700'
                                }>
                                  {task.status?.replace('_', ' ')}
                                </Badge>
                                <AIProcessingIndicator taskId={task.id} compact />
                              </div>
                            </div>
                            {task.created_at && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Created: {format(new Date(task.created_at), 'MMM d, yyyy h:mm a')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="text-center py-12">
                      <Bot className="h-16 w-16 text-purple-200 mx-auto mb-4" />
                      <h3 className="font-medium text-lg mb-2">No AI Tasks Yet</h3>
                      <p className="text-muted-foreground text-sm mb-4">
                        Create an AI task to generate sales materials, reports, or analysis for this company
                      </p>
                      <Button onClick={() => setShowAITaskDialog(true)} variant="outline">
                        <Sparkles className="h-4 w-4 mr-2" />
                        Create Your First AI Task
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* AI Analysis Tab */}
            <TabsContent value="ai-analysis" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Brain className="h-5 w-5 text-blue-600" />
                      AI Company Analysis
                    </CardTitle>
                    <CardDescription>
                      Generate AI-powered insights about sales prospects and support strategies
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={generateAIAnalysis} 
                    disabled={loadingAnalysis}
                    data-testid="generate-ai-analysis-btn"
                  >
                    {loadingAnalysis ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        {aiAnalysis ? 'Regenerate Analysis' : 'Generate Analysis'}
                      </>
                    )}
                  </Button>
                </CardHeader>
                <CardContent>
                  {analysisError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                      <p className="text-red-700 text-sm">{analysisError}</p>
                    </div>
                  )}
                  
                  {aiAnalysis ? (
                    <div className="space-y-6">
                      {/* Quick Action: Add All as Tasks */}
                      {(aiAnalysis.sales_prospects?.opportunities?.length > 0 || 
                        aiAnalysis.support_strategies?.recommendations?.length > 0 ||
                        aiAnalysis.suggested_tasks?.length > 0) && (
                        <div className="flex justify-end">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={createAllSuggestedTasks}
                            data-testid="add-all-tasks-btn"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add All as Tasks
                          </Button>
                        </div>
                      )}

                      {/* Sales Prospects */}
                      {aiAnalysis.sales_prospects && (
                        <div className="space-y-3">
                          <h4 className="font-semibold flex items-center gap-2">
                            <Target className="h-4 w-4 text-emerald-600" />
                            Sales Opportunities
                          </h4>
                          <div className="grid gap-3">
                            {aiAnalysis.sales_prospects.opportunities?.map((opp, i) => {
                              const taskId = `sales_opportunity-${opp.title}`;
                              const isCreated = createdTaskIds.has(taskId);
                              return (
                                <div key={i} className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <p className="font-medium text-emerald-800">{opp.title}</p>
                                      <p className="text-sm text-emerald-700">{opp.description}</p>
                                      <div className="flex items-center gap-2 mt-2">
                                        {opp.estimated_value && (
                                          <Badge variant="outline" className="border-emerald-300">
                                            Est. Value: {formatCurrency(opp.estimated_value)}
                                          </Badge>
                                        )}
                                        {opp.confidence && (
                                          <Badge variant="outline" className="border-emerald-300 capitalize">
                                            {opp.confidence} confidence
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      variant={isCreated ? "ghost" : "outline"}
                                      size="sm"
                                      onClick={() => createTaskFromSuggestion(opp, 'sales_opportunity')}
                                      disabled={isCreated}
                                      className={isCreated ? "text-emerald-600" : ""}
                                      data-testid={`add-task-opp-${i}`}
                                    >
                                      {isCreated ? (
                                        <CheckCircle className="h-4 w-4" />
                                      ) : (
                                        <>
                                          <Plus className="h-4 w-4 mr-1" />
                                          Task
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              );
                            }) || (
                              <p className="text-sm text-muted-foreground">{aiAnalysis.sales_prospects.summary || 'No specific opportunities identified.'}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Support Strategies */}
                      {aiAnalysis.support_strategies && (
                        <div className="space-y-3">
                          <h4 className="font-semibold flex items-center gap-2">
                            <Lightbulb className="h-4 w-4 text-amber-600" />
                            Support Strategies
                          </h4>
                          <div className="grid gap-3">
                            {aiAnalysis.support_strategies.recommendations?.map((rec, i) => {
                              const taskId = `support_strategy-${rec.title || rec}`;
                              const isCreated = createdTaskIds.has(taskId);
                              return (
                                <div key={i} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <p className="font-medium text-amber-800">{rec.title || rec}</p>
                                      {rec.description && (
                                        <p className="text-sm text-amber-700">{rec.description}</p>
                                      )}
                                      {rec.priority && (
                                        <Badge variant="outline" className="mt-2 border-amber-300 capitalize">
                                          {rec.priority} priority
                                        </Badge>
                                      )}
                                    </div>
                                    <Button
                                      variant={isCreated ? "ghost" : "outline"}
                                      size="sm"
                                      onClick={() => createTaskFromSuggestion(rec, 'support_strategy')}
                                      disabled={isCreated}
                                      className={isCreated ? "text-amber-600" : ""}
                                      data-testid={`add-task-rec-${i}`}
                                    >
                                      {isCreated ? (
                                        <CheckCircle className="h-4 w-4" />
                                      ) : (
                                        <>
                                          <Plus className="h-4 w-4 mr-1" />
                                          Task
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              );
                            }) || (
                              <p className="text-sm text-muted-foreground">{aiAnalysis.support_strategies.summary || 'No specific strategies identified.'}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* AI Suggested Tasks */}
                      {aiAnalysis.suggested_tasks?.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="font-semibold flex items-center gap-2">
                            <Zap className="h-4 w-4 text-purple-600" />
                            Suggested Next Actions
                          </h4>
                          <div className="grid gap-3">
                            {aiAnalysis.suggested_tasks.map((task, i) => {
                              const taskId = `suggested_task-${task.title || task}`;
                              const isCreated = createdTaskIds.has(taskId);
                              return (
                                <div key={i} className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <p className="font-medium text-purple-800">{task.title || task}</p>
                                      {task.description && (
                                        <p className="text-sm text-purple-700">{task.description}</p>
                                      )}
                                      {task.due_date && (
                                        <Badge variant="outline" className="mt-2 border-purple-300">
                                          Suggested by: {task.due_date}
                                        </Badge>
                                      )}
                                    </div>
                                    <Button
                                      variant={isCreated ? "ghost" : "outline"}
                                      size="sm"
                                      onClick={() => createTaskFromSuggestion(task, 'suggested_task')}
                                      disabled={isCreated}
                                      className={isCreated ? "text-purple-600" : ""}
                                      data-testid={`add-task-suggested-${i}`}
                                    >
                                      {isCreated ? (
                                        <CheckCircle className="h-4 w-4" />
                                      ) : (
                                        <>
                                          <Plus className="h-4 w-4 mr-1" />
                                          Task
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Key Insights */}
                      {aiAnalysis.key_insights && (
                        <div className="space-y-3">
                          <h4 className="font-semibold flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-blue-600" />
                            Key Insights
                          </h4>
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <ul className="space-y-2">
                              {(Array.isArray(aiAnalysis.key_insights) ? aiAnalysis.key_insights : [aiAnalysis.key_insights]).map((insight, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-blue-800">
                                  <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                  {typeof insight === 'string' ? insight : insight.text || JSON.stringify(insight)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* Raw Analysis Fallback */}
                      {!aiAnalysis.sales_prospects && !aiAnalysis.support_strategies && !aiAnalysis.key_insights && (
                        <div className="p-4 bg-slate-50 border rounded-lg">
                          <pre className="text-sm whitespace-pre-wrap">
                            {typeof aiAnalysis === 'string' ? aiAnalysis : JSON.stringify(aiAnalysis, null, 2)}
                          </pre>
                        </div>
                      )}

                      {aiAnalysis.generated_at && (
                        <p className="text-xs text-muted-foreground text-right">
                          Generated: {format(new Date(aiAnalysis.generated_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Brain className="h-16 w-16 text-blue-200 mx-auto mb-4" />
                      <h3 className="font-medium text-lg mb-2">No Analysis Generated</h3>
                      <p className="text-muted-foreground text-sm mb-4">
                        Click the button above to generate AI-powered insights for this company
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {company.recent_activities?.length > 0 ? (
                    <div className="space-y-4">
                      {company.recent_activities.map((activity, i) => (
                        <div key={i} className="flex items-start gap-3 pb-3 border-b last:border-0">
                          <div className="p-2 bg-slate-100 rounded-full">
                            <Clock className="h-4 w-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="text-sm">
                              <span className="font-medium capitalize">{activity.action}</span>
                              {' '}on {activity.entity_type}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {activity.timestamp ? format(new Date(activity.timestamp), 'MMM d, yyyy h:mm a') : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No recent activity</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Playbooks Tab */}
            <TabsContent value="playbooks" className="mt-4">
              <PlaybookNotesSection 
                entityType="company" 
                entityId={companyId}
                className="w-full"
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Edit Company Dialog */}
      <Dialog open={editCompanyDialog} onOpenChange={setEditCompanyDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>Update company information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveCompany} className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm(p => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Legal Name</Label>
                <Input
                  value={companyForm.legal_name}
                  onChange={(e) => setCompanyForm(p => ({ ...p, legal_name: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Industry</Label>
                <Input
                  value={companyForm.industry}
                  onChange={(e) => setCompanyForm(p => ({ ...p, industry: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={companyForm.website}
                  onChange={(e) => setCompanyForm(p => ({ ...p, website: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={companyForm.phone}
                  onChange={(e) => setCompanyForm(p => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={companyForm.email}
                  onChange={(e) => setCompanyForm(p => ({ ...p, email: e.target.value }))}
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="space-y-2">
              <Label>Street Address</Label>
              <Input
                value={companyForm.address || ''}
                onChange={(e) => setCompanyForm(p => ({ ...p, address: e.target.value }))}
                placeholder="Street address"
                data-testid="company-address-input"
              />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={companyForm.city || ''}
                  onChange={(e) => setCompanyForm(p => ({ ...p, city: e.target.value }))}
                  placeholder="City"
                  data-testid="company-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label>State/Province</Label>
                <Input
                  value={companyForm.state || ''}
                  onChange={(e) => setCompanyForm(p => ({ ...p, state: e.target.value }))}
                  placeholder="State"
                  data-testid="company-state-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Postal Code</Label>
                <Input
                  value={companyForm.postal_code || ''}
                  onChange={(e) => setCompanyForm(p => ({ ...p, postal_code: e.target.value }))}
                  placeholder="Postal/ZIP"
                  data-testid="company-postal-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input
                  value={companyForm.country || ''}
                  onChange={(e) => setCompanyForm(p => ({ ...p, country: e.target.value }))}
                  placeholder="Country"
                  data-testid="company-country-input"
                />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={companyForm.status} onValueChange={(v) => setCompanyForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Account Type</Label>
                <Select value={companyForm.account_type} onValueChange={(v) => setCompanyForm(p => ({ ...p, account_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="competitor">Competitor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tier</Label>
                <Select value={companyForm.tier || "none"} onValueChange={(v) => setCompanyForm(p => ({ ...p, tier: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                    <SelectItem value="mid_market">Mid-Market</SelectItem>
                    <SelectItem value="smb">SMB</SelectItem>
                    <SelectItem value="startup">Startup</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Key Contact</Label>
                <Select value={companyForm.key_contact_id || "none"} onValueChange={(v) => setCompanyForm(p => ({ ...p, key_contact_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {linkedContacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employees</Label>
                <Input
                  type="number"
                  value={companyForm.employee_count}
                  onChange={(e) => setCompanyForm(p => ({ ...p, employee_count: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Annual Revenue</Label>
                <Input
                  type="number"
                  value={companyForm.annual_revenue}
                  onChange={(e) => setCompanyForm(p => ({ ...p, annual_revenue: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Select 
                  value={companyForm.payment_terms || "none"} 
                  onValueChange={(v) => setCompanyForm(p => ({ ...p, payment_terms: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="payment-terms-select"><SelectValue placeholder="Select terms" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                    <SelectItem value="Net 7">Net 7</SelectItem>
                    <SelectItem value="Net 15">Net 15</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                    <SelectItem value="Net 45">Net 45</SelectItem>
                    <SelectItem value="Net 60">Net 60</SelectItem>
                    <SelectItem value="Net 90">Net 90</SelectItem>
                    <SelectItem value="2/10 Net 30">2/10 Net 30</SelectItem>
                    <SelectItem value="COD">COD (Cash on Delivery)</SelectItem>
                    <SelectItem value="Prepaid">Prepaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Customer Awareness Level</Label>
                <Select 
                  value={companyForm.awareness_level || "none"} 
                  onValueChange={(v) => setCompanyForm(p => ({ ...p, awareness_level: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="awareness-level-select"><SelectValue placeholder="Auto-detected" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Auto-detect</SelectItem>
                    <SelectItem value="new">🆕 New</SelectItem>
                    <SelectItem value="aware">👁 Aware</SelectItem>
                    <SelectItem value="active">✓ Active</SelectItem>
                    <SelectItem value="engaged">🔥 Engaged</SelectItem>
                    <SelectItem value="at_risk">⚠ At Risk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={companyForm.description}
                onChange={(e) => setCompanyForm(p => ({ ...p, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={companyForm.notes}
                onChange={(e) => setCompanyForm(p => ({ ...p, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditCompanyDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unit Dialog */}
      <Dialog open={unitDialog} onOpenChange={setUnitDialog}>
        <DialogContent className="max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingUnit ? 'Edit Unit' : 'Add Unit'}</DialogTitle>
            <DialogDescription>
              {editingUnit ? 'Update unit information' : 'Add a new unit, branch, or location'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveUnit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit Name *</Label>
                <Input
                  value={unitForm.name}
                  onChange={(e) => setUnitForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., West Coast Branch"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Type</Label>
                <Select value={unitForm.unit_type} onValueChange={(v) => setUnitForm(p => ({ ...p, unit_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="branch">Branch</SelectItem>
                    <SelectItem value="division">Division</SelectItem>
                    <SelectItem value="department">Department</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                    <SelectItem value="subsidiary">Subsidiary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={unitForm.address}
                onChange={(e) => setUnitForm(p => ({ ...p, address: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={unitForm.city}
                  onChange={(e) => setUnitForm(p => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={unitForm.state}
                  onChange={(e) => setUnitForm(p => ({ ...p, state: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input
                  value={unitForm.country}
                  onChange={(e) => setUnitForm(p => ({ ...p, country: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={unitForm.phone}
                  onChange={(e) => setUnitForm(p => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={unitForm.email}
                  onChange={(e) => setUnitForm(p => ({ ...p, email: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Manager Contact</Label>
                <Select value={unitForm.manager_contact_id || "none"} onValueChange={(v) => setUnitForm(p => ({ ...p, manager_contact_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {linkedContacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Employees</Label>
                <Input
                  type="number"
                  value={unitForm.employee_count}
                  onChange={(e) => setUnitForm(p => ({ ...p, employee_count: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_hq"
                checked={unitForm.is_headquarters}
                onChange={(e) => setUnitForm(p => ({ ...p, is_headquarters: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="is_hq" className="cursor-pointer">This is the headquarters</Label>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={unitForm.notes}
                onChange={(e) => setUnitForm(p => ({ ...p, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUnitDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : (editingUnit ? 'Update Unit' : 'Create Unit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create AI Task Dialog */}
      <Dialog open={showAITaskDialog} onOpenChange={setShowAITaskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              Create AI Task
            </DialogTitle>
            <DialogDescription>
              Assign a task to an AI agent for {company?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAITask} className="space-y-4">
            <div className="space-y-2">
              <Label>Task Title *</Label>
              <Input
                value={aiTaskForm.title}
                onChange={(e) => setAiTaskForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g., Generate sales proposal"
                data-testid="ai-task-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={aiTaskForm.description}
                onChange={(e) => setAiTaskForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Describe what you want the AI to accomplish..."
                rows={3}
                data-testid="ai-task-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-purple-600" />
                Assign to AI Agent *
              </Label>
              <Select 
                value={aiTaskForm.assigned_agent_id || "none"} 
                onValueChange={(val) => setAiTaskForm(p => ({ ...p, assigned_agent_id: val === "none" ? '' : val }))}
              >
                <SelectTrigger data-testid="ai-task-agent-select">
                  <SelectValue placeholder="Select AI agent..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select an AI Agent --</SelectItem>
                  {aiAgents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name || agent.display_name} - {agent.role_title || 'AI Assistant'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Output Format</Label>
                <Select 
                  value={aiTaskForm.deliverable_type} 
                  onValueChange={(val) => setAiTaskForm(p => ({ ...p, deliverable_type: val }))}
                >
                  <SelectTrigger data-testid="ai-task-deliverable-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Word Document (.docx)</SelectItem>
                    <SelectItem value="spreadsheet">Excel Spreadsheet (.xlsx)</SelectItem>
                    <SelectItem value="report">Detailed Report (.docx)</SelectItem>
                    <SelectItem value="email_draft">Email Draft (.docx)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={aiTaskForm.priority} 
                  onValueChange={(val) => setAiTaskForm(p => ({ ...p, priority: val }))}
                >
                  <SelectTrigger data-testid="ai-task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAITaskDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={creatingAITask || !aiTaskForm.title.trim() || !aiTaskForm.assigned_agent_id}
                data-testid="create-ai-task-submit"
              >
                {creatingAITask ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Create Task
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Task Detail Dialog */}
      <Dialog open={showTaskDetail} onOpenChange={setShowTaskDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              AI Task: {selectedTask?.title}
            </DialogTitle>
            <DialogDescription>
              {selectedTask?.assigned_to_name || 'AI Agent'} • {selectedTask?.status?.replace('_', ' ')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-4">
              {/* Task Status */}
              <div className="flex items-center gap-2">
                <AIProcessingIndicator 
                  taskId={selectedTask.id} 
                  onComplete={(status) => {
                    if (status === 'completed') {
                      fetchCompanyTasks();
                    }
                  }}
                />
              </div>

              {/* Description */}
              {selectedTask.description && (
                <div className="space-y-2">
                  <Label className="font-semibold">Description</Label>
                  <p className="text-sm text-muted-foreground bg-slate-50 p-3 rounded-lg">
                    {selectedTask.description}
                  </p>
                </div>
              )}

              {/* AI Reasoning */}
              {taskAIHistory?.ai_logs?.length > 0 && (
                <div className="space-y-2">
                  <Label className="font-semibold flex items-center gap-2">
                    <Brain className="h-4 w-4 text-purple-600" />
                    AI Analysis
                  </Label>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    {taskAIHistory.ai_logs.map((log, i) => (
                      <div key={i} className="space-y-2">
                        {log.reasoning && (
                          <div>
                            <p className="text-xs font-medium text-purple-700 mb-1">Reasoning:</p>
                            <p className="text-sm text-purple-800 whitespace-pre-wrap">{log.reasoning}</p>
                          </div>
                        )}
                        {log.analysis_summary && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-purple-700 mb-1">Summary:</p>
                            <p className="text-sm text-purple-800">{log.analysis_summary}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deliverables */}
              {taskAIHistory?.deliverables?.length > 0 && (
                <div className="space-y-2">
                  <Label className="font-semibold flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    Deliverables
                  </Label>
                  <div className="space-y-2">
                    {taskAIHistory.deliverables.map((deliverable) => (
                      <div 
                        key={deliverable.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-sm">{deliverable.title || deliverable.filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {deliverable.file_type?.toUpperCase()} • {format(new Date(deliverable.created_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownloadDeliverable(deliverable)}
                          data-testid={`download-deliverable-${deliverable.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No AI Work Yet */}
              {!taskAIHistory?.has_ai_work && selectedTask.status === 'pending' && (
                <div className="text-center py-6 bg-amber-50 rounded-lg border border-amber-200">
                  <Loader2 className="h-8 w-8 text-amber-600 mx-auto mb-2 animate-spin" />
                  <p className="text-amber-800 font-medium">AI is processing this task...</p>
                  <p className="text-amber-700 text-sm">Deliverables will appear here when ready</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={productDialog} onOpenChange={setProductDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
            <DialogDescription>
              Track products, equipment, and assets for this company
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Product Name *</Label>
                <Input
                  value={productForm.product_name}
                  onChange={(e) => setProductForm(p => ({ ...p, product_name: e.target.value }))}
                  placeholder="e.g., Server X500"
                  data-testid="product-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Serial Number *</Label>
                <Input
                  value={productForm.serial_number}
                  onChange={(e) => setProductForm(p => ({ ...p, serial_number: e.target.value }))}
                  placeholder="e.g., SN-12345-ABC"
                  data-testid="product-serial-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Product Type</Label>
                <Select value={productForm.product_type} onValueChange={(v) => setProductForm(p => ({ ...p, product_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hardware">Hardware</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="subscription">Subscription</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Model Number</Label>
                <Input
                  value={productForm.model_number}
                  onChange={(e) => setProductForm(p => ({ ...p, model_number: e.target.value }))}
                  placeholder="Model/Part number"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={productForm.status} onValueChange={(v) => setProductForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="warranty">Under Warranty</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="pending_activation">Pending Activation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Support Level</Label>
                <Select value={productForm.support_level} onValueChange={(v) => setProductForm(p => ({ ...p, support_level: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Purchase Date</Label>
                <Input
                  type="date"
                  value={productForm.purchase_date}
                  onChange={(e) => setProductForm(p => ({ ...p, purchase_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Warranty Expiry</Label>
                <Input
                  type="date"
                  value={productForm.warranty_expiry}
                  onChange={(e) => setProductForm(p => ({ ...p, warranty_expiry: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Support Expiry</Label>
                <Input
                  type="date"
                  value={productForm.support_expiry}
                  onChange={(e) => setProductForm(p => ({ ...p, support_expiry: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={productForm.location}
                  onChange={(e) => setProductForm(p => ({ ...p, location: e.target.value }))}
                  placeholder="e.g., Server Room A"
                />
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={productForm.quantity}
                  onChange={(e) => setProductForm(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>License Key</Label>
              <Input
                value={productForm.license_key}
                onChange={(e) => setProductForm(p => ({ ...p, license_key: e.target.value }))}
                placeholder="Software license key (if applicable)"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={productForm.notes}
                onChange={(e) => setProductForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Additional notes about this product..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveProduct} 
              disabled={saving || !productForm.product_name || !productForm.serial_number}
              data-testid="save-product-btn"
            >
              {saving ? 'Saving...' : editingProduct ? 'Update Product' : 'Add Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Contact Dialog (Bug #2 fix) */}
      <Dialog open={linkContactDialog} onOpenChange={setLinkContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Link Existing Contact</DialogTitle>
            <DialogDescription>
              Select a contact to link to {company?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Contact</Label>
              <Select value={selectedContactToLink} onValueChange={setSelectedContactToLink}>
                <SelectTrigger data-testid="link-contact-select">
                  <SelectValue placeholder="Choose a contact..." />
                </SelectTrigger>
                <SelectContent>
                  {unlinkedContacts.length > 0 ? (
                    unlinkedContacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        <div className="flex items-center gap-2">
                          <span>{contact.first_name} {contact.last_name}</span>
                          {contact.email && (
                            <span className="text-xs text-muted-foreground">({contact.email})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>No unlinked contacts available</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Only showing contacts not currently linked to a company
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkContactDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleLinkContact} 
              disabled={linkingContact || !selectedContactToLink}
              data-testid="confirm-link-contact-btn"
            >
              {linkingContact ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Linking...
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4 mr-2" />
                  Link Contact
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyProfilePage;
